import { formatDialect, postgresql } from 'sql-formatter';
import type { FormatOptionsWithDialect } from 'sql-formatter';

export type SqlTokenKind =
  | 'function'
  | 'identifier'
  | 'keyword'
  | 'number'
  | 'param'
  | 'plain'
  | 'string';

export interface SqlToken {
  kind: SqlTokenKind;
  text: string;
}

/** One display line. `indent` counts levels, not characters. */
export interface SqlLine {
  indent: number;
  tokens: SqlToken[];
}

const INDENT_TEXT = '  ';

/**
 * A placeholder list longer than this shows only its first and last entry. A
 * report query binds one placeholder per document, so the list can run to
 * hundreds.
 */
const MAX_INLINE_PARAMS = 3;

/**
 * `expressionWidth` is how long a parenthesised expression may be before it
 * opens onto several lines. At the default of 50 most `NULLIF(... ->> 'answer', '')`
 * calls the report queries make would open up.
 */
const SQL_FORMAT_OPTIONS: FormatOptionsWithDialect = {
  dialect: postgresql,
  expressionWidth: 80,
  keywordCase: 'upper',
  logicalOperatorNewline: 'before',
  tabWidth: INDENT_TEXT.length,
};

/** Words the SQL view colors as keywords. Case is left to the formatter. */
const KEYWORDS: ReadonlySet<string> = new Set([
  'ALL',
  'AND',
  'ANY',
  'ARRAY',
  'AS',
  'ASC',
  'AT',
  'BETWEEN',
  'BY',
  'CASE',
  'DESC',
  'DISTINCT',
  'ELSE',
  'END',
  'EXISTS',
  'FALSE',
  'FILTER',
  'FIRST',
  'FROM',
  'GROUP',
  'HAVING',
  'ILIKE',
  'IN',
  'INNER',
  'IS',
  'JOIN',
  'LAST',
  'LATERAL',
  'LEFT',
  'LIKE',
  'LIMIT',
  'NOT',
  'NULL',
  'NULLS',
  'OFFSET',
  'ON',
  'OR',
  'ORDER',
  'OUTER',
  'RIGHT',
  'SELECT',
  'THEN',
  'TIME',
  'TRUE',
  'UNION',
  'WHEN',
  'WHERE',
  'WITH',
  'ZONE',
]);

/**
 * One alternative per token shape, tried in order. Quoted strings and quoted
 * identifiers come before everything else so a keyword, comma or parenthesis
 * inside them is never read as syntax. The closing quote is optional so an
 * unterminated literal still lands in one token. The last alternative takes any
 * character the others skipped, so no input is dropped.
 */
const TOKEN_PATTERN =
  /(\s+)|('(?:[^']|'')*'?)|("(?:[^"]|"")*"?)|(\$\d+)|(\d+(?:\.\d+)?)|([A-Za-z_][A-Za-z0-9_]*)|([(),;])|([^\s'"$\dA-Za-z_(),;]+)|([\s\S])/g;

const SPACE: SqlToken = { kind: 'plain', text: ' ' };
const PARAM_GAP: SqlToken = { kind: 'plain', text: ' … ' };

interface CollapsedSql {
  /** The last placeholder of each shortened list, keyed by the first one, which stays in the SQL. */
  lastParamByFirst: ReadonlyMap<string, string>;
  sql: string;
}

/**
 * Lays out the SQL an analysis tool call ran, for display.
 *
 * The server renders the query as one line. `sql-formatter` breaks it into a
 * line per clause, projected column and condition, and each line is then
 * tokenized so the card can color it. Text the formatter cannot parse comes
 * back on one line.
 *
 * The output is the same query. Only whitespace and keyword case change. With
 * `collapseParams` a long placeholder list is also shortened, which is for
 * reading and not for copying.
 */
export function formatAnalysisSql(
  sql: string,
  { collapseParams = false }: { collapseParams?: boolean } = {}
): SqlLine[] {
  const source = sql.trim();
  if (!source) return [];

  const collapsed = collapseParams
    ? collapseParamRuns(source)
    : { lastParamByFirst: new Map<string, string>(), sql: source };
  return getFormattedSql(collapsed.sql)
    .split('\n')
    .filter(line => line.trim())
    .map(line => getSqlLine(line, collapsed.lastParamByFirst));
}

/** The formatted query as plain text, two spaces per indent level. */
export function getSqlText(lines: SqlLine[]): string {
  return lines
    .map(line => INDENT_TEXT.repeat(line.indent) + line.tokens.map(token => token.text).join(''))
    .join('\n');
}

export function tokenizeSql(sql: string): SqlToken[] {
  const source = sql.trim();
  const tokens: SqlToken[] = [];
  for (const match of source.matchAll(TOKEN_PATTERN)) {
    const [text, whitespace, quotedString, quotedIdentifier, param, numeric, word] = match;
    if (whitespace) tokens.push(SPACE);
    else if (quotedString) tokens.push({ kind: 'string', text });
    else if (quotedIdentifier) tokens.push({ kind: 'identifier', text });
    else if (param) tokens.push({ kind: 'param', text });
    else if (numeric) tokens.push({ kind: 'number', text });
    else if (word) tokens.push(getWordToken(word, source.slice(match.index + text.length)));
    else tokens.push({ kind: 'plain', text });
  }
  return tokens;
}

function getFormattedSql(sql: string): string {
  try {
    return formatDialect(sql, SQL_FORMAT_OPTIONS);
  } catch {
    return getTokenText(tokenizeSql(sql));
  }
}

function getSqlLine(line: string, lastParamByFirst: ReadonlyMap<string, string>): SqlLine {
  const indentWidth = line.length - line.trimStart().length;
  return {
    indent: Math.floor(indentWidth / INDENT_TEXT.length),
    tokens: tokenizeSql(line).flatMap(token => expandCollapsedParam(token, lastParamByFirst)),
  };
}

function expandCollapsedParam(
  token: SqlToken,
  lastParamByFirst: ReadonlyMap<string, string>
): SqlToken[] {
  const lastParam = token.kind === 'param' ? lastParamByFirst.get(token.text) : undefined;
  if (!lastParam) return [token];
  return [token, PARAM_GAP, { kind: 'param', text: lastParam }];
}

function getWordToken(word: string, rest: string): SqlToken {
  if (KEYWORDS.has(word.toUpperCase())) return { kind: 'keyword', text: word };
  if (/^\s*\(/.test(rest)) return { kind: 'function', text: word };
  return { kind: 'plain', text: word };
}

function getTokenText(tokens: SqlToken[]): string {
  return tokens.map(token => token.text).join('');
}

function getIsPlain(token: SqlToken | undefined, text: string): boolean {
  return token?.kind === 'plain' && token.text === text;
}

function getIsSpace(token: SqlToken | undefined): boolean {
  return getIsPlain(token, SPACE.text);
}

/** Index just past the placeholder list that starts at `start`, or `start` if none does. */
function getParamRunEnd(tokens: SqlToken[], start: number): number {
  if (tokens[start]?.kind !== 'param') return start;

  let end = start + 1;
  while (getIsPlain(tokens[end], ',')) {
    const next = getIsSpace(tokens[end + 1]) ? end + 2 : end + 1;
    if (tokens[next]?.kind !== 'param') break;
    end = next + 1;
  }
  return end;
}

/**
 * Leaves only the first placeholder of each long list in the SQL, before it is
 * formatted. The formatter gives each entry of a list too long for one line a
 * line of its own, so a 400-document query would otherwise run to 400 lines.
 */
function collapseParamRuns(sql: string): CollapsedSql {
  const tokens = tokenizeSql(sql);
  const kept: SqlToken[] = [];
  const lastParamByFirst = new Map<string, string>();
  let index = 0;
  while (index < tokens.length) {
    const runEnd = getParamRunEnd(tokens, index);
    const params = tokens.slice(index, runEnd).filter(token => token.kind === 'param');
    const firstParam = params.at(0);
    const lastParam = params.at(-1);
    if (params.length > MAX_INLINE_PARAMS && firstParam && lastParam) {
      kept.push(firstParam);
      lastParamByFirst.set(firstParam.text, lastParam.text);
      index = runEnd;
    } else {
      kept.push(tokens[index]);
      index += 1;
    }
  }
  return { lastParamByFirst, sql: getTokenText(kept) };
}
